import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["orderPairs", "pairs", "submit", "paymentType", "paymentTypeDisplay", "paymentTypeSelect", "colppySyncGroup", "colppySyncCheckbox", "colppySyncHint", "percepcionIibb", "percepcionIibbGroup"];

  connect() {
    // Validar formulario al cargar
    this.validateForm();

    // Escuchar cambios en los inputs de montos
    this.element.addEventListener('input', () => {
      this.updatePairs();
      this.validateForm();
    });

    // Inicializar variables para control de debounce
    this.invoiceSelectionTimeout = null;
    this.invoiceSelectionInProgress = false;

    // Cerrar el modal de Bootstrap cuando el formulario se envía vía Turbo
    // para evitar que el backdrop y body.modal-open queden huérfanos
    this.boundHandleSubmitStart = this.handleSubmitStart.bind(this);
    this.element.addEventListener('turbo:submit-start', this.boundHandleSubmitStart);

    // Si hay una orden inicial, mostrar el campo de forma de pago y calcular IIBB
    const initialOrderId = this.element.querySelector('[data-received-payment-form-target="initialOrderId"]');
    if (initialOrderId && initialOrderId.value) {
      this.updatePaymentTypeField(initialOrderId.value);
      this.autoCalculatePercepcionIibb();
    }
  }

  disconnect() {
    if (this.boundHandleSubmitStart) {
      this.element.removeEventListener('turbo:submit-start', this.boundHandleSubmitStart);
    }
    if (this.invoiceSelectionTimeout) {
      clearTimeout(this.invoiceSelectionTimeout);
    }
  }

  handleSubmitStart() {
    // Cerrar el modal de Bootstrap inmediatamente al enviar el formulario
    // Esto previene que el backdrop y body.modal-open persistan después de
    // que Turbo reemplace el body con la respuesta del redirect
    const modal = this.element.closest('.modal');
    if (modal && typeof $ !== 'undefined' && $.fn.modal) {
      // Desactivar animación para cierre instantáneo
      $(modal).removeClass('fade');
      $(modal).modal('hide');
    }
    // Limpieza forzada por si modal('hide') no completa a tiempo
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
  }

  openModal() {
    // Abrir el modal cuando Turbo carga el contenido (Bootstrap 4 con jQuery)
    const modal = document.getElementById('createPaymentModal');
    if (modal && typeof $ !== 'undefined' && $.fn.modal) {
      // Remover aria-hidden antes de mostrar el modal para evitar warnings de accesibilidad
      modal.removeAttribute('aria-hidden');
      $(modal).modal('show');
    }
  }

  updateOrderAmountFields() {
    const orderSelect = document.querySelector("select[name='received_payment[order_ids][]']");
    if (!orderSelect) return;

    // Obtener IDs seleccionados: SlimSelect mantiene sincronizado el select original
    // Por lo tanto, podemos usar directamente selectedOptions del select original
    const selectedOrderIds = Array.from(orderSelect.selectedOptions)
      .map(opt => opt.value)
      .filter(id => id !== ''); // Filtrar valores vacíos

    // Buscar elementos dentro del contexto del formulario para evitar problemas con Turbo Frames
    const form = this.element;
    const orderAmountsList = form.querySelector("#order-amounts-list");
    const orderAmountsDiv = form.querySelector("#order-amounts");

    if (selectedOrderIds.length > 0) {
      if (orderAmountsDiv) orderAmountsDiv.style.display = "block";
      if (orderAmountsList) {
        orderAmountsList.innerHTML = "";

        selectedOrderIds.forEach((orderId) => {
          const option = orderSelect.querySelector(`option[value="${orderId}"]`);
          if (!option) return;

          const optionText = option.textContent;
          const parts = optionText.split('|');
          const orderInfo = parts[0]?.trim() || `Orden #${orderId}`;

          // Extraer disponible restante (última parte después de "Disponible:")
          const availablePart = parts.find(p => p.includes('Disponible:'));
          const availableMatch = availablePart ? availablePart.match(/Disponible:\s*\$?\s*([\d.]+)/) : null;
          const availablePrice = availableMatch ? parseFloat(availableMatch[1].replace(/\./g, '')) : 0;

          // Obtener el precio total y disponible desde data attributes si están disponibles
          const orderPriceCents = option.dataset.orderPrice ? parseInt(option.dataset.orderPrice) : 0;
          const orderRemainingCents = option.dataset.orderRemaining ? parseInt(option.dataset.orderRemaining) : availablePrice * 100;
          const availablePriceArs = parseFloat((orderRemainingCents / 100.0).toFixed(2));

          // Formatear para mostrar (siempre con 2 decimales)
          const availableDisplay = availablePriceArs.toFixed(2);

          // Crear campo para monto parcial
          const amountDiv = document.createElement("div");
          amountDiv.className = "form-group";
          const inputValue = availablePriceArs > 0 ? availablePriceArs.toFixed(2) : '0.00';
          amountDiv.innerHTML = `
            <label>${orderInfo} - Disponible: $${availableDisplay}</label>
            <input type="number"
                   name="order_amount_${orderId}"
                   class="form-control order-amount-input"
                   min="0"
                   step="0.01"
                   max="${availablePriceArs}"
                   value="${inputValue}"
                   data-order-id="${orderId}"
                   data-order-price="${orderPriceCents}"
                   data-remaining="${orderRemainingCents}"
                   data-action="input->received-payment-form#updateOrderPairs blur->received-payment-form#formatInputValue">
          `;
          orderAmountsList.appendChild(amountDiv);

          // Formatear el input para mostrar 2 decimales
          const input = amountDiv.querySelector('input');
          if (input) {
            input.addEventListener('blur', () => {
              const value = parseFloat(input.value) || 0;
              input.value = value.toFixed(2);
            });
          }
        });
      }

      // Auto-calcular percepción IIBB
      this.autoCalculatePercepcionIibb();

      // Agregar campo de forma de pago dinámicamente si no existe
      this.updatePaymentTypeField(selectedOrderIds[0]);

      // Cargar facturas de las órdenes seleccionadas
      // Usar debounce para evitar múltiples llamadas
      if (this.invoiceSelectionTimeout) {
        clearTimeout(this.invoiceSelectionTimeout);
        this.invoiceSelectionTimeout = null;
      }
      this.invoiceSelectionTimeout = setTimeout(() => {
        // Verificar nuevamente que todavía hay órdenes seleccionadas antes de hacer la llamada
        const currentOrderSelect = document.querySelector("select[name='received_payment[order_ids][]']");
        if (currentOrderSelect) {
          const currentSelectedIds = Array.from(currentOrderSelect.selectedOptions)
            .map(opt => opt.value)
            .filter(id => id !== '');
          if (currentSelectedIds.length > 0) {
            this.updateInvoiceSelection(currentSelectedIds);
          }
        }
      }, 300);
    } else {
      // Cancelar cualquier llamada pendiente a updateInvoiceSelection
      if (this.invoiceSelectionTimeout) {
        clearTimeout(this.invoiceSelectionTimeout);
        this.invoiceSelectionTimeout = null;
      }

      if (orderAmountsDiv) orderAmountsDiv.style.display = "none";

      // Ocultar percepción IIBB si no hay órdenes
      if (this.hasPercepcionIibbGroupTarget) {
        this.percepcionIibbGroupTarget.style.display = "none";
      }

      // Limpiar y ocultar el campo de forma de pago si no hay órdenes seleccionadas
      const paymentTypeGroup = document.getElementById('payment-type-group');
      if (paymentTypeGroup) {
        // Limpiar los valores de los campos dentro del grupo
        if (this.hasPaymentTypeTarget) {
          this.paymentTypeTarget.value = '';
        }
        if (this.hasPaymentTypeDisplayTarget) {
          this.paymentTypeDisplayTarget.value = '';
        }
        if (this.hasPaymentTypeSelectTarget) {
          this.paymentTypeSelectTarget.value = '';
        }
        paymentTypeGroup.style.display = 'none';
      }
      const receiptFileGroup = document.getElementById('receipt-file-group');
      if (receiptFileGroup) {
        receiptFileGroup.style.display = 'none';
      }

      // Limpiar y ocultar la sección de facturas
      const invoiceSelectionGroup = document.getElementById('invoice-selection-group');
      if (invoiceSelectionGroup) {
        // Remover el atributo style inline para evitar conflictos
        invoiceSelectionGroup.removeAttribute('style');
        invoiceSelectionGroup.style.setProperty('display', 'none', 'important');
      }

      // Ocultar el div.form-group que contiene el label y el select de facturas
      const invoiceSelectContainer = document.getElementById('invoice-select-container');
      if (invoiceSelectContainer) {
        const formGroup = invoiceSelectContainer.closest('.form-group');
        if (formGroup) {
          // Remover el atributo style inline para evitar conflictos
          formGroup.removeAttribute('style');
          formGroup.style.setProperty('display', 'none', 'important');
        }
        // Limpiar el contenido del contenedor
        invoiceSelectContainer.innerHTML = '';
      }

      // Limpiar los inputs de montos de facturas
      const invoiceAmountsList = document.getElementById("invoice-amounts-list");
      const invoiceAmountsDiv = document.getElementById("invoice-amounts");
      if (invoiceAmountsList) {
        invoiceAmountsList.innerHTML = '';
      }
      if (invoiceAmountsDiv) {
        invoiceAmountsDiv.style.display = 'none';
      }

      // Limpiar el campo hidden de facturas
      if (this.hasPairsTarget) {
        this.pairsTarget.value = JSON.stringify({});
      }

      // Ocultar opción de Colppy
      if (this.hasColppySyncGroupTarget) {
        this.colppySyncGroupTarget.style.display = "none";
      }
    }

    this.updateOrderPairs();
    this.validateForm();
  }

  async updateInvoiceSelection(orderIds) {
    // Evitar múltiples llamadas simultáneas
    if (this.invoiceSelectionInProgress) return;

    const patientId = this.getPatientId();
    // Asegurar que orderIds sea un array
    const orderIdsArray = Array.isArray(orderIds) ? orderIds : (orderIds ? [orderIds] : []);

    if (!patientId || orderIdsArray.length === 0) return;

    this.invoiceSelectionInProgress = true;

    try {
      const response = await fetch(`/patients/${patientId}/invoices_and_payments/invoices_for_orders?order_ids[]=${orderIdsArray.join('&order_ids[]=')}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        const invoices = await response.json();
        this.displayInvoiceSelect(invoices);
      }
    } finally {
      this.invoiceSelectionInProgress = false;
    }
  }

  displayInvoiceSelect(invoices) {
    // Verificar primero si hay órdenes seleccionadas antes de mostrar facturas
    const orderSelect = document.querySelector("select[name='received_payment[order_ids][]']");
    if (orderSelect) {
      const selectedOrderIds = Array.from(orderSelect.selectedOptions)
        .map(opt => opt.value)
        .filter(id => id !== '');

      // Si no hay órdenes seleccionadas, no mostrar facturas
      if (selectedOrderIds.length === 0) {
        return;
      }
    }

    const invoiceSelectionGroup = document.getElementById('invoice-selection-group');
    const invoiceSelectContainer = document.getElementById('invoice-select-container');

    if (invoices.length > 0 && invoiceSelectionGroup && invoiceSelectContainer) {
      invoiceSelectionGroup.style.setProperty('display', 'block', 'important');

      // Mostrar el form-group que contiene el select
      const formGroup = invoiceSelectContainer.closest('.form-group');
      if (formGroup) {
        formGroup.style.setProperty('display', 'block', 'important');
      }

      // Verificar si ya existe un select
      const existingSelect = invoiceSelectContainer.querySelector("select[name='received_payment[invoice_ids][]']");

      if (existingSelect) {
        // Si ya existe, solo actualizar las opciones sin reemplazar todo
        existingSelect.innerHTML = '';
        invoices.forEach(invoice => {
          const option = document.createElement('option');
          option.value = invoice.id;
          // Usar "Disponible" en lugar de "Pendiente" si está disponible
          const availableText = invoice.available !== undefined ? `Disponible: $${invoice.available}` : `Pendiente: $${invoice.pending}`;
          option.textContent = `Factura #${invoice.id} | ${invoice.date} | Total: $${invoice.total} | ${availableText}`;
          // Usar los centavos enviados por el backend para evitar errores de parseo
          // Usar pending_cents para el límite del input (pendiente proporcional)
          if (invoice.pending_cents !== undefined) {
            option.dataset.invoiceRemaining = invoice.pending_cents;
          }
          if (invoice.synced_to_colppy) option.dataset.syncedToColppy = "true";
          if (invoice.registered_from_colppy) option.dataset.registeredFromColppy = "true";
          existingSelect.appendChild(option);
        });
      } else {
        // Si no existe, crear el select
        let selectHTML = `<select name="received_payment[invoice_ids][]"
                                  multiple="multiple"
                                  class="form-control"
                                  data-controller="slim-select"
                                  data-slim-target="field"
                                  data-received-payment-form-target="invoiceSelect"
                                  data-action="change->received-payment-form#updateAmountFields">`;

        invoices.forEach(invoice => {
          const pendingCentsAttr = invoice.pending_cents !== undefined ? ` data-invoice-remaining="${invoice.pending_cents}"` : '';
          const colppyAttrs = (invoice.synced_to_colppy ? ' data-synced-to-colppy="true"' : '') +
                              (invoice.registered_from_colppy ? ' data-registered-from-colppy="true"' : '');
          // Usar "Disponible" en lugar de "Pendiente" si está disponible
          const availableText = invoice.available !== undefined ? `Disponible: $${invoice.available}` : `Pendiente: $${invoice.pending}`;
          selectHTML += `<option value="${invoice.id}"${pendingCentsAttr}${colppyAttrs}>Factura #${invoice.id} | ${invoice.date} | Total: $${invoice.total} | ${availableText}</option>`;
        });

        selectHTML += '</select>';
        invoiceSelectContainer.innerHTML = selectHTML;
      }

      // Guardar metadata de Colppy para uso al seleccionar facturas
      this._invoiceColppyData = {};
      invoices.forEach(invoice => {
        this._invoiceColppyData[invoice.id] = {
          synced_to_colppy: invoice.synced_to_colppy || false,
          registered_from_colppy: invoice.registered_from_colppy || false
        };
      });
    } else if (invoiceSelectionGroup) {
      invoiceSelectionGroup.style.setProperty('display', 'none', 'important');
      const formGroup = invoiceSelectContainer?.closest('.form-group');
      if (formGroup) {
        formGroup.style.setProperty('display', 'none', 'important');
      }
    }
  }

  updateAmountFields() {
    // Buscar dentro del contenedor #invoice-select-container los .ss-value
    // SlimSelect muestra las opciones seleccionadas como .ss-value dentro de .ss-main
    let selectedInvoiceIds = [];
    let invoiceSelect = null; // Guardar el select correcto aquí

    const invoiceSelectContainer = document.getElementById('invoice-select-container');
    if (invoiceSelectContainer) {
      // Obtener el select correcto desde el contenedor
      invoiceSelect = invoiceSelectContainer.querySelector("select[name='received_payment[invoice_ids][]']");

      // Buscar el .ss-main dentro del contenedor
      const ssMain = invoiceSelectContainer.querySelector('.ss-main');
      if (ssMain) {
        // Buscar todos los .ss-value que representan opciones seleccionadas
        const ssValues = ssMain.querySelectorAll('.ss-value');
        ssValues.forEach(ssValue => {
          // Extraer el ID de la factura del texto: "Factura #14 | ..."
          const valueText = ssValue.querySelector('.ss-value-text');
          if (valueText) {
            const optionText = valueText.textContent.trim();
            const match = optionText.match(/Factura #(\d+)/);
            if (match) {
              selectedInvoiceIds.push(match[1]);
            }
          }
        });
      }
    }

    // Si no encontramos el select en el contenedor, buscarlo en el form o documento
    if (!invoiceSelect) {
      const form = this.element.closest('form');
      if (form) {
        invoiceSelect = form.querySelector("select[name='received_payment[invoice_ids][]']");
      }
      if (!invoiceSelect) {
        invoiceSelect = document.querySelector("select[name='received_payment[invoice_ids][]']");
      }
    }

    if (!invoiceSelect) {
      console.error('❌ invoiceSelect not found');
      return;
    }

    // Si no se encontró desde SlimSelect, usar el select original como fallback
    if (selectedInvoiceIds.length === 0) {
      selectedInvoiceIds = Array.from(invoiceSelect.selectedOptions || [])
        .map(opt => opt.value)
        .filter(id => id !== '');
    }

    const invoiceAmountsList = document.getElementById("invoice-amounts-list");
    const invoiceAmountsDiv = document.getElementById("invoice-amounts");

    if (selectedInvoiceIds.length > 0 && invoiceAmountsDiv && invoiceAmountsList) {
      invoiceAmountsDiv.style.display = "block";
      invoiceAmountsList.innerHTML = "";

      selectedInvoiceIds.forEach((invoiceId) => {
        const option = invoiceSelect.querySelector(`option[value="${invoiceId}"]`);
        if (!option) {
          console.warn('⚠️ Option not found for invoice ID:', invoiceId);
          return;
        }

        // Obtener el monto pendiente desde data attribute si está disponible
        const invoiceRemainingCents = option.dataset.invoiceRemaining ? parseInt(option.dataset.invoiceRemaining) : 0;


        let pendingAmountFloat = 0;
        if (invoiceRemainingCents > 0) {
          pendingAmountFloat = parseFloat((invoiceRemainingCents / 100.0).toFixed(2));
        } else {
          // Fallback: extraer del texto
          const optionText = option.textContent;
          const parts = optionText.split('|');
          const pendingPart = parts.find(p => p.includes('Pendiente:'));
          if (pendingPart) {
            // Extraer el número, manejando formato con puntos o comas
            const pendingMatch = pendingPart.match(/Pendiente:\s*\$?\s*([\d,]+\.?\d*)/);
            if (pendingMatch) {
              // Reemplazar coma por punto para parseFloat (formato argentino: 425,25 -> 425.25)
              const priceStr = pendingMatch[1].replace(',', '.');
              pendingAmountFloat = parseFloat(priceStr) || 0;
            }
          }
        }

        const amountDiv = document.createElement("div");
        amountDiv.className = "form-group";
        const inputValue = pendingAmountFloat > 0 ? pendingAmountFloat.toFixed(2) : '0.00';

        amountDiv.innerHTML = `
          <label>Factura #${invoiceId} - Pendiente: $${pendingAmountFloat.toFixed(2)}</label>
          <input type="number"
                 name="invoice_amount_${invoiceId}"
                 class="form-control invoice-amount-input"
                 min="0"
                 step="0.01"
                 max="${pendingAmountFloat}"
                 value="${inputValue}"
                 data-invoice-id="${invoiceId}"
                 data-action="input->received-payment-form#updatePairs blur->received-payment-form#formatInputValue">
        `;
        invoiceAmountsList.appendChild(amountDiv);

        // Formatear el input para mostrar 2 decimales
        const input = amountDiv.querySelector('input');
        if (input) {
          input.addEventListener('blur', () => {
            const value = parseFloat(input.value) || 0;
            input.value = value.toFixed(2);
          });
        }
      });
    } else if (invoiceAmountsDiv) {
      invoiceAmountsDiv.style.display = "none";
    }

    this.updateColppySyncOption();
    this.updatePairs();
    this.validateForm();
  }

  updateColppySyncOption() {
    if (!this.hasColppySyncGroupTarget) return;

    // Determinar qué facturas están seleccionadas
    const invoiceSelectContainer = document.getElementById('invoice-select-container');
    if (!invoiceSelectContainer) {
      this.colppySyncGroupTarget.style.display = "none";
      return;
    }

    const invoiceSelect = invoiceSelectContainer.querySelector("select[name='received_payment[invoice_ids][]']");
    if (!invoiceSelect) {
      this.colppySyncGroupTarget.style.display = "none";
      return;
    }

    const selectedOptions = Array.from(invoiceSelect.selectedOptions || []).filter(opt => opt.value !== '');
    if (selectedOptions.length === 0) {
      this.colppySyncGroupTarget.style.display = "none";
      return;
    }

    // Verificar si alguna factura seleccionada está sincronizada con Colppy
    let hasSyncedToColppy = false;
    let hasImportedFromColppy = false;

    selectedOptions.forEach(option => {
      const invoiceId = option.value;
      const colppyData = this._invoiceColppyData && this._invoiceColppyData[invoiceId];
      if (colppyData) {
        if (colppyData.synced_to_colppy) hasSyncedToColppy = true;
        if (colppyData.registered_from_colppy) hasImportedFromColppy = true;
      } else {
        // Fallback: leer desde data attributes
        if (option.dataset.syncedToColppy === "true") hasSyncedToColppy = true;
        if (option.dataset.registeredFromColppy === "true") hasImportedFromColppy = true;
      }
    });

    if (hasSyncedToColppy) {
      this.colppySyncGroupTarget.style.display = "block";
      // Default: desmarcado si la factura fue importada desde Colppy (cobro probablemente ya existe)
      // Marcado si la factura fue creada desde el sistema (cobro aún no existe en Colppy)
      if (this.hasColppySyncCheckboxTarget) {
        this.colppySyncCheckboxTarget.checked = !hasImportedFromColppy;
      }
    } else {
      this.colppySyncGroupTarget.style.display = "none";
    }
  }

  updatePaymentTypeField(firstOrderId) {
    const paymentTypeGroup = document.getElementById('payment-type-group');
    const receiptFileGroup = document.getElementById('receipt-file-group');

    if (!firstOrderId) {
      // Ocultar campos si no hay órdenes seleccionadas
      if (paymentTypeGroup) paymentTypeGroup.style.display = 'none';
      if (receiptFileGroup) receiptFileGroup.style.display = 'none';
      return;
    }

    const orderSelect = document.querySelector("select[name='received_payment[order_ids][]']");
    if (!orderSelect) return;

    const option = orderSelect.querySelector(`option[value="${firstOrderId}"]`);
    if (!option) return;

    // Obtener payment_type del data attribute
    const paymentType = option.dataset.paymentType;

    // El comprobante se puede adjuntar siempre que haya una orden seleccionada:
    // no depende de que la orden traiga forma de pago. Las consolidadas se crean
    // sin Payment y antes se quedaban sin comprobante Y sin forma de pago.
    if (receiptFileGroup) {
      receiptFileGroup.style.display = 'block';
    }

    if (paymentTypeGroup) {
      paymentTypeGroup.style.display = 'block';
    }

    // Mapeo de payment_type a texto en español
    const paymentTypeDisplayMap = {
      'cash': 'Efectivo',
      'credit_card': 'Tarjeta de crédito',
      'debit_card': 'Tarjeta de débito',
      'wire': 'Transferencia',
      'bank_check': 'Cheque'
    };

    if (paymentType) {
      // La orden ya tiene forma de pago: se muestra pre-poblada y no editable.
      if (this.hasPaymentTypeTarget) this.paymentTypeTarget.value = paymentType;
      if (this.hasPaymentTypeDisplayTarget) {
        this.paymentTypeDisplayTarget.value = paymentTypeDisplayMap[paymentType] || paymentType;
        this.paymentTypeDisplayTarget.style.display = 'block';
      }
      if (this.hasPaymentTypeSelectTarget) this.paymentTypeSelectTarget.style.display = 'none';
    } else {
      // Orden sin Payment (consolidadas): la elige el operador.
      if (this.hasPaymentTypeDisplayTarget) {
        this.paymentTypeDisplayTarget.value = '';
        this.paymentTypeDisplayTarget.style.display = 'none';
      }
      if (this.hasPaymentTypeSelectTarget) {
        this.paymentTypeSelectTarget.style.display = 'block';
        if (this.hasPaymentTypeTarget) {
          this.paymentTypeTarget.value = this.paymentTypeSelectTarget.value || '';
        }
      }
    }

    this.validateForm();
  }

  // El select no manda su propio name: la única fuente del payment_type enviado
  // es el hidden, así que hay que copiarlo a mano.
  selectPaymentType(event) {
    if (this.hasPaymentTypeTarget) {
      this.paymentTypeTarget.value = event.target.value || '';
    }
    this.validateForm();
  }

  getPatientId() {
    const form = this.element.closest('form');
    if (form) {
      const action = form.action;
      const match = action.match(/\/patients\/(\d+)\//);
      return match ? match[1] : null;
    }
    return null;
  }

  updateOrderPairs() {
    // Actualizar pares de órdenes
    const orderInputs = this.element.querySelectorAll('.order-amount-input');
    const orderPairs = {};
    const orderIds = [];
    orderInputs.forEach((input) => {
      const orderId = input.dataset.orderId;
      // Asegurar que el valor se interprete correctamente como número decimal
      // Reemplazar comas por puntos si las hay (formato europeo)
      const rawValue = input.value.replace(',', '.');
      const value = parseFloat(rawValue) || 0;
      if (value > 0 && orderId) {
        // Convertir de pesos a centavos
        const valueInCents = Math.round(value * 100);
        orderPairs[orderId] = valueInCents;
        orderIds.push(orderId);
      }
    });
    if (this.hasOrderPairsTarget) {
      this.orderPairsTarget.value = JSON.stringify(orderPairs);
    }
    // Auto-calcular percepción IIBB cuando cambian los montos de órdenes
    this.autoCalculatePercepcionIibb();
    // Actualizar selección de facturas si hay órdenes seleccionadas
    if (orderIds.length > 0) {
      this.updateInvoiceSelection(orderIds);
    }
  }

  updatePairs() {
    // Actualizar pares de facturas
    const invoiceInputs = this.element.querySelectorAll('.invoice-amount-input');
    const invoicePairs = {};
    invoiceInputs.forEach((input) => {
      const invoiceId = input.dataset.invoiceId;
      const value = parseFloat(input.value) || 0;
      if (value > 0 && invoiceId) {
        invoicePairs[invoiceId] = Math.round(value * 100); // Convertir a cents
      }
    });
    if (this.hasPairsTarget) {
      this.pairsTarget.value = JSON.stringify(invoicePairs);
    }
  }

  formatInputValue(event) {
    const input = event.target;
    const value = parseFloat(input.value) || 0;
    input.value = value.toFixed(2);
  }

  // Auto-calculate IIBB perception based on total order amounts and patient rate
  autoCalculatePercepcionIibb() {
    if (!this.hasPercepcionIibbTarget) return;

    const totalOrders = this.getTotalOrderAmounts();

    // Show/hide the group based on whether orders are selected
    if (this.hasPercepcionIibbGroupTarget) {
      this.percepcionIibbGroupTarget.style.display = totalOrders > 0 ? "block" : "none";
    }

    // Auto-calculate only if rate is configured
    const rate = parseFloat(this.percepcionIibbTarget.dataset.iibbRate) || 0;
    if (rate > 0) {
      // Calculate on neto (without IVA 21%) - order amounts include IVA
      const neto = totalOrders / 1.21;
      const percepcion = (neto * rate / 100);
      this.percepcionIibbTarget.value = percepcion.toFixed(2);
    }
  }

  // Recalculate button handler
  recalculatePercepcionIibb() {
    this.autoCalculatePercepcionIibb();
  }

  // Manual edit handler (just keeps the value, no auto-recalc)
  updatePercepcionIibb() {
    // No-op: allows manual override without auto-recalculating
  }

  // Get total order amounts in ARS (not cents)
  getTotalOrderAmounts() {
    const orderInputs = this.element.querySelectorAll('.order-amount-input');
    let total = 0;
    orderInputs.forEach((input) => {
      const rawValue = input.value.replace(',', '.');
      total += parseFloat(rawValue) || 0;
    });
    return total;
  }

  validateForm() {
    const orderInputs = this.element.querySelectorAll('.order-amount-input');
    let hasValidInputs = false;

    orderInputs.forEach((input) => {
      const value = parseFloat(input.value) || 0;
      if (value > 0) {
        hasValidInputs = true;
      }
    });

    // Si la forma de pago quedó a elección del operador (orden sin Payment),
    // no dejamos enviar hasta que elija una: el server la exige igual y el
    // error volvería después de perder el archivo adjunto.
    const needsPaymentType = this.hasPaymentTypeSelectTarget &&
      this.paymentTypeSelectTarget.style.display !== 'none' &&
      !(this.hasPaymentTypeTarget && this.paymentTypeTarget.value);

    if (this.hasSubmitTarget) {
      if (hasValidInputs && !needsPaymentType) {
        this.submitTarget.removeAttribute("disabled");
      } else {
        this.submitTarget.setAttribute("disabled", "");
      }
    }
  }

  submit(event) {
    event.preventDefault();
    this.updatePairs();
    this.validateForm();

    if (!this.submitTarget.hasAttribute('disabled')) {
      this.element.submit();
    }
  }

  closeModal() {
    const modal = document.getElementById('createPaymentModal');
    if (modal && typeof $ !== 'undefined' && $.fn.modal) {
      $(modal).modal('hide');
    }

    // Quitar el foco del modal antes de cerrarlo
    const activeElement = document.activeElement;
    if (activeElement && modal.contains(activeElement)) {
      activeElement.blur();
    }
    if (activeElement === modal) {
      modal.blur();
    }

    // Mover el foco al body
    document.body.focus();

    // Cerrar el modal con Bootstrap
    if (typeof $ !== 'undefined' && $.fn.modal) {
      $(modal).modal('hide');
    }
  }
}
